/*
 * First 50 gift-with-purchase.
 *
 * Keeps the gift product in sync with the cart, on the cart page and in the
 * cart drawer:
 * - a product tagged "first-50-promo" is in the cart -> the gift is added
 * - no tagged product remains -> the gift is removed, so it can never be
 *   checked out on its own
 * - the gift is limited to a single unit -> any extra quantity a shopper
 *   adds is reset back to one
 * - if a shopper removes the gift, it stays removed; it is only re-added
 *   when the rest of the cart changes (tracked by otherItemsSignature),
 *   not on plain page loads
 *
 * Cart state is read server-side by the first-50-gift-data section (fetched
 * through the Section Rendering API), because product tags are not exposed by
 * /cart.js. The gift product is selected in theme settings ("First 50 promo
 * gift").
 */

(() => {
	const SOURCE = 'first-50-gift'
	const STATE_SECTION_ID = 'first-50-gift-data'
	// non-gift cart signature the gift was last added/present for; a change
	// signals the rest of the cart moved on and the gift may be re-added
	const ADDED_SIG_KEY = 'first50:addedForSig'

	class First50GiftManager {
		constructor() {
			this.processing = false
			this.rerun = false

			if (
				typeof subscribe === 'function' &&
				typeof PUB_SUB_EVENTS !== 'undefined'
			) {
				subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
					if (event && event.source === SOURCE) return
					this.reconcile()
				})
			}

			this.reconcile()
		}

		async reconcile() {
			if (this.processing) {
				this.rerun = true
				return
			}

			this.processing = true
			try {
				let guard = 0
				do {
					this.rerun = false
					await this.reconcileOnce()
					guard++
				} while (this.rerun && guard < 3)
			} catch (error) {
				console.error('[first-50-gift]', error)
			} finally {
				this.processing = false
			}
		}

		async reconcileOnce() {
			const state = await this.fetchState()
			if (!state) return

			const quantities = state.giftLineQuantities || []
			const giftLines = state.giftLineKeys.map((key, index) => ({
				key,
				// missing/blank quantity -> assume 1 so it is not wrongly "fixed"
				quantity: parseInt(quantities[index], 10) || 1,
			}))
			const giftInCart = giftLines.length > 0
			const currentSig = state.otherItemsSignature || ''

			if (!state.promoInCart) {
				// no qualifying product: the gift has no reason to exist
				this.setAddedSig(null)
				if (giftInCart) await this.removeGift(giftLines.map((line) => line.key))
				return
			}

			if (giftInCart) {
				// gift belongs to this cart context; remember it and keep it single
				this.setAddedSig(currentSig)
				await this.enforceSingleGift(giftLines)
				return
			}

			// qualifying product present but no gift. Only add it when the rest of
			// the cart has changed since the gift was last present/added — so a
			// shopper who deliberately removed the gift is not re-given it on a
			// plain reload or a gift-only change.
			if (this.getAddedSig() !== currentSig) {
				if (!state.giftVariantId || !state.giftAvailable) return
				await this.addGift(state.giftVariantId)
				this.setAddedSig(currentSig)
			}
		}

		getAddedSig() {
			try {
				return sessionStorage.getItem(ADDED_SIG_KEY)
			} catch (error) {
				return this.addedSig ?? null
			}
		}

		setAddedSig(value) {
			this.addedSig = value
			try {
				if (value === null) {
					sessionStorage.removeItem(ADDED_SIG_KEY)
				} else {
					sessionStorage.setItem(ADDED_SIG_KEY, value)
				}
			} catch (error) {
				// sessionStorage unavailable (private mode / disabled): in-memory only
			}
		}

		// keep exactly one gift line at quantity 1; reset extra quantity and
		// drop any duplicate gift lines
		async enforceSingleGift(giftLines) {
			const needsFix =
				giftLines.length > 1 || giftLines.some((line) => line.quantity !== 1)
			if (!needsFix) return

			const updates = {}
			giftLines.forEach((line, index) => {
				updates[line.key] = index === 0 ? 1 : 0
			})
			await this.updateCart(updates)
		}

		async fetchState() {
			const response = await fetch(
				`${routes.cart_url}?section_id=${STATE_SECTION_ID}`
			)
			if (!response.ok) return null

			const text = await response.text()
			const dataElement = new DOMParser()
				.parseFromString(text, 'text/html')
				.querySelector('[data-first-50-gift-data]')
			if (!dataElement) return null

			try {
				return JSON.parse(dataElement.textContent)
			} catch (error) {
				return null
			}
		}

		async addGift(variantId) {
			const sections = this.getSectionsToRender()
			const body = JSON.stringify({
				items: [{ id: variantId, quantity: 1 }],
				sections: this.getSectionNames(sections),
				sections_url: window.location.pathname,
			})

			const response = await fetch(`${routes.cart_add_url}`, {
				...fetchConfig(),
				...{ body },
			})
			const parsedState = await response.json()
			if (parsedState.status) {
				console.error('[first-50-gift]', parsedState.description)
				return
			}

			this.renderSections(sections, parsedState.sections)
			this.afterCartChanged(false)
		}

		async removeGift(lineKeys) {
			const updates = {}
			lineKeys.forEach((key) => {
				updates[key] = 0
			})
			await this.updateCart(updates)
		}

		async updateCart(updates) {
			const sections = this.getSectionsToRender()
			const body = JSON.stringify({
				updates,
				sections: this.getSectionNames(sections),
				sections_url: window.location.pathname,
			})

			const response = await fetch(`${routes.cart_update_url}`, {
				...fetchConfig(),
				...{ body },
			})
			if (!response.ok) return
			const parsedState = await response.json()

			this.renderSections(sections, parsedState.sections)
			this.afterCartChanged(parsedState.item_count === 0)
		}

		getSectionsToRender() {
			const sections = []

			const mainCartItems = document.getElementById('main-cart-items')
			if (mainCartItems) {
				sections.push({
					id: 'main-cart-items',
					section: mainCartItems.dataset.id,
					selector: '.js-contents',
				})

				const cartFooter = document.getElementById('main-cart-footer')
				if (cartFooter) {
					sections.push({
						id: 'main-cart-footer',
						section: cartFooter.dataset.id,
						selector: '.js-contents-totals',
					})
				}

				const cartShipping = document.getElementById('main-cart-shipping')
				if (cartShipping) {
					sections.push({
						id: 'main-cart-shipping',
						section: cartShipping.dataset.id,
						selector: '.js-contents-shipping',
					})
				}
			}

			if (
				document.querySelector('cart-drawer') &&
				document.getElementById('CartDrawer')
			) {
				sections.push({
					id: 'CartDrawer',
					section: 'cart-drawer',
					selector: '.drawer__inner',
				})
			}

			if (document.getElementById('cart-icon-bubble')) {
				sections.push({
					id: 'cart-icon-bubble',
					section: 'cart-icon-bubble',
					selector: '.shopify-section',
				})
			}

			return sections.filter((section) => section.section)
		}

		getSectionNames(sections) {
			return [...new Set(sections.map((section) => section.section))]
		}

		renderSections(sections, renderedSections) {
			if (!renderedSections) return

			sections.forEach((section) => {
				const container = document.getElementById(section.id)
				const html = renderedSections[section.section]
				if (!container || !html) return

				const source = new DOMParser()
					.parseFromString(html, 'text/html')
					.querySelector(section.selector)
				if (!source) return

				const target = container.querySelector(section.selector) || container
				target.innerHTML = source.innerHTML
			})
		}

		afterCartChanged(isEmpty) {
			document
				.querySelectorAll('cart-items, cart-drawer-items')
				.forEach((element) => element.classList.toggle('is-empty', isEmpty))

			const cartFooter = document.getElementById('main-cart-footer')
			if (cartFooter) cartFooter.classList.toggle('is-empty', isEmpty)

			const cartDrawerWrapper = document.querySelector('cart-drawer')
			if (cartDrawerWrapper)
				cartDrawerWrapper.classList.toggle('is-empty', isEmpty)

			if (document.querySelector('.cart-shipping')) {
				document
					.querySelectorAll('cart-items, cart-drawer-items')
					.forEach((element) => {
						if (typeof element.cartShipping === 'function' && element.minTotal) {
							try {
								element.cartShipping()
							} catch (error) {
								// progress bar refresh is cosmetic; never block reconciliation
							}
						}
					})
			}
		}
	}

	const init = () => {
		if (!window.first50GiftManager) {
			window.first50GiftManager = new First50GiftManager()
		}
	}

	if (document.readyState === 'loading') {
		document.addEventListener('DOMContentLoaded', init)
	} else {
		init()
	}
})()
